import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import Container from '../components/common/Container';
import { useTranslation } from 'react-i18next';
import InputSearchBox from '../components/common/InputSearchBox';
import DateSearchBox from '../components/common/DateSearchBox';
import Button from '../components/common/Button';
import LegalPersonReferenceDetailModal from '../components/modal/LegalPersonReferenceDetailModal';
import MemoModal from '../components/modal/MemoModal';
import SelectSearchBox from '../components/common/SelectSearchBox';
import { Status, VerificationType } from '../types/legalPersonVerification';
import LegalPersonReferenceItem from '../components/tableItems/legalPersonReferenceItem';
import { GET_LEGAL_PERSON_REFERENCE } from '../graphql/queries';
import { SAVE_VERIFICATION_MEMO } from '../graphql/mutations';
import Loading from '../components/common/Loading';

type SortField = 'corporateName' | 'corporateNumber' | 'applicantName' | 'receivedDate' | 'verificationDate' | 'legalPersonStatus' | 'amlStatus' | 'memo';
type SortDirection = 'asc' | 'desc';

const LegalPersonReference: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('verification');

    const [ corporateNumber, setCorporateNumber] = useState<string>('');
    const [ startDate, setStartDate ] = useState<string>('');
    const [ endDate, setEndDate ] = useState<string>('');
    const [ verificationType, setVerificationType ] = useState<VerificationType | 'all'>('all');
    const [ status, setStatus ] = useState<Status | 'all'>('all');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [selectedPage, setSelectedPage] = useState<number>(1);
    const [fieldName, setFieldName] = useState<string>('');
    const [sortField, setSortField] = useState<SortField>('receivedDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const { loading, error, data, refetch } = useQuery(GET_LEGAL_PERSON_REFERENCE);

    const sortedData = useMemo(() => {
        if (!data || !data.allLegalPersonReference) return [];
        
        const sorted = [...data.allLegalPersonReference].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'corporateName':
                    aValue = a.corporateName || '';
                    bValue = b.corporateName || '';
                    break;
                case 'corporateNumber':
                    aValue = a.corporateNumber || '';
                    bValue = b.corporateNumber || '';
                    break;
                case 'applicantName':
                    aValue = a.applicantName || '';
                    bValue = b.applicantName || '';
                    break;
                case 'receivedDate':
                    aValue = new Date(a.legalPersonReference_receivedDate || 0);
                    bValue = new Date(b.legalPersonReference_receivedDate || 0);
                    break;
                case 'verificationDate':
                    aValue = new Date(a.legalPersonReference_verificationDate || 0);
                    bValue = new Date(b.legalPersonReference_verificationDate || 0);
                    break;
                case 'legalPersonStatus':
                    aValue = a.legalPersonReference_status === "verified" ? a.legalPersonReference_result : a.legalPersonReference_status;
                    bValue = b.legalPersonReference_status === "verified" ? b.legalPersonReference_result : b.legalPersonReference_status;
                    break;
                case 'amlStatus':
                    aValue = a.amlRiskCheck_status === "completed" ? a.amlRiskCheck_result : a.amlRiskCheck_status;
                    bValue = b.amlRiskCheck_status === "completed" ? b.amlRiskCheck_result : b.amlRiskCheck_status;
                    break;
                case 'memo':
                    aValue = a.legalPersonReference_verificationMemo || '';
                    bValue = b.legalPersonReference_verificationMemo || '';
                    break;
                default:
                    return 0;
            }
            
            if (aValue instanceof Date && bValue instanceof Date) {
                return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
            
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
        
        return sorted;
    }, [data, sortField, sortDirection]);

    useEffect(() => {
        if(sortedData) {
            setTotalPage(Math.max(1, Math.ceil(sortedData.length / 10)));
        }
    }, [sortedData]);

     const [saveVerificationMemo] = useMutation(SAVE_VERIFICATION_MEMO, {
        onCompleted: () => {
            console.log('Legal person reference created successfully');
            window.location.reload();
        },
        onError: (error) => {
            console.error('Error creating legal person reference:', error);
            alert(t('error creating legal person reference'));
        }
    });

    const isValidVerificationType = (value: string): value is VerificationType | 'all' => {
        return Object.values(VerificationType).includes(value as VerificationType) || value === 'all';
    }

    const verificationTypeOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(VerificationType).map(method => ({
            value: method,
            label: t(method)
        }))
    ], [t]);

    const isValidStatus = (value: string): value is Status | 'all' => {
        return Object.values(Status).includes(value as Status) || value === 'all';
    }

    const statusOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(Status).map(method => ({
            value: method,
            label: t(method)
        }))
    ], [t]);

    const handleCloseDetailModal = useCallback(() => setIsDetailModalOpen(false), []);
    const handleCloseMemoModal = useCallback(() => setIsMemoModalOpen(false), []);

    const handleUpdateMemo = (value: string) => {
        saveVerificationMemo({
            variables: {
                verificationMemo: value,
                verificationPublicId: data.allLegalPersonReference[selectedIndex].legalPersonReference_verificationPublicId,
            }
        })
        .then(
            () => setIsMemoModalOpen(false)
        ).catch(
            err => console.log(err)
        )
    }

    const handleOpenDetailModal = useCallback((idx: number) => {
        setSelectedIndex((selectedPage - 1) * 10 + idx);
        setIsDetailModalOpen(true);
        setFieldName("legalPersonReference");
    }, [selectedPage]);
    const onHandlePrevPage = useCallback(() => setSelectedPage(prev => prev - 1 < 1 ? 1 : prev - 1), []);
    const onHandleNextPage = useCallback(() => setSelectedPage(prev => prev + 1 > totalPage ? prev : prev + 1), [totalPage]);
    const handleBack = useCallback(() => navigate('/corporate-reference'), [navigate]);
    const handleCorporateNumber = useCallback((value: string) => {
        setCorporateNumber(value);
    }, []);
    const handleStartDate = useCallback((value: string) => {
        setStartDate(value);
    }, []);
    const handleEndDate = useCallback((value: string) => {
        setEndDate(value);
    }, []);
    const handleState = useCallback((value: string) => {
        if (isValidStatus(value)) {
            setStatus(value);
        } else {
            console.error('Invalid Status:', value);
        }
    }, []);
    const handleVerificationType = useCallback((value: string) => {
        if (isValidVerificationType(value)) {
            setVerificationType(value);
        } else {
            console.error('Invalid Verification Type:', value);
        }
    }, []);

    const handleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setSelectedPage(1);
    }, [sortField, sortDirection]);

    const getSortIcon = useCallback((field: SortField) => {
        if (sortField !== field) return ' ↕️';
        return sortDirection === 'asc' ? ' 🔼' : ' 🔽';
    }, [sortField, sortDirection]);

    if (loading) return <Loading />;
    if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

    return (
        <Container label={t('legal person reference history')}>
            <LegalPersonReferenceDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                referenceDetailData={sortedData && sortedData && sortedData.length > 0 ? sortedData[selectedIndex] : null}
                fieldName={fieldName}
            />
            <MemoModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                onConfirm={handleUpdateMemo}
                memoData={sortedData && sortedData[selectedIndex].legalPersonReference_verificationMemo}
                companyName={sortedData && sortedData[selectedIndex].corporateName}
            />
            <div className="search-form">
                <InputSearchBox
                    inputValue={corporateNumber}
                    placeholder={t('search by company name or company number')}
                    onChange={handleCorporateNumber}
                />
                <DateSearchBox
                    label={t('start date')}
                    inputValue={startDate}
                    onChange={handleStartDate}
                />
                <DateSearchBox
                    label={t('end date')}
                    inputValue={endDate}
                    onChange={handleEndDate}
                />
                <SelectSearchBox
                    label={t('status')}
                    options={statusOptions}
                    selectedValue={status}
                    onChange={handleState}
                />
                <SelectSearchBox
                    options={verificationTypeOptions}
                    selectedValue={verificationType}
                    onChange={handleVerificationType}
                />
                <div className="ml-20 inline-block">
                    <Button
                        label={t('search')}
                        onClick={() => {
                            console.log('Search with:', {
                                corporateNumber,
                                startDate,
                                endDate,
                                status,
                                verificationType
                            });
                            refetch();
                            setSelectedPage(1)
                        }}
                    />
                </div>
                <div className="pl-20 inline-block cursor-pointer" 
                    onClick={() => {
                        console.log('Resetting search criteria');
                        setCorporateNumber('');
                        setStartDate('');
                        setEndDate('');
                        setStatus('all');
                        setVerificationType('all');
                        refetch();
                        setSelectedPage(1)
                        window.location.reload();
                    }}>
                    <span className="underline">{t('reset')}</span>
                </div>
            </div>
            <table>
                <thead>
                <tr>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('corporateName')}
                    >
                        {t('corporate name')}{getSortIcon('corporateName')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('corporateNumber')}
                    >
                        {t('corporate number')}{getSortIcon('corporateNumber')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('applicantName')}
                    >
                        {t('reference person')}{getSortIcon('applicantName')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('receivedDate')}
                    >
                        {t('reference datetime')}{getSortIcon('receivedDate')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('verificationDate')}
                    >
                        {t('verified datetime')}{getSortIcon('verificationDate')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('legalPersonStatus')}
                    >
                        {t('legal person verification status')}{getSortIcon('legalPersonStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('amlStatus')}
                    >
                        {t('aml risk check status')}{getSortIcon('amlStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('memo')}
                    >
                        メモ{getSortIcon('memo')}
                    </th>
                </tr>
                </thead>
                <tbody>
                {sortedData.filter((_: any, index: number) => (index >= (selectedPage - 1) * 10) && (index < selectedPage * 10)).map((value: any, index: number) => {
                    return (
                        <LegalPersonReferenceItem
                            key={index}
                            item={value}
                            selectedPage={selectedPage}
                            idx={index}
                            showDetail={handleOpenDetailModal}
                            showMemoModal={(value) => {
                                setSelectedIndex(value)
                                setIsMemoModalOpen(true)
                            }}
                        />
                    )
                })}
                </tbody>
            </table>
            <div className="pagination">
                <Button
                    label={t('prev page')}
                    onClick={onHandlePrevPage}
                    addedClass='mx-[10px]'
                />
                <span>{selectedPage}</span>
                &nbsp;/&nbsp;
                <span>{totalPage}</span>
                <Button
                    label={t('next page')}
                    onClick={onHandleNextPage}
                    addedClass='mx-[10px]'
                />
            </div>
            <Button
                label={t('back')}
                onClick={handleBack}
            />
        </Container>
    );
};

export default LegalPersonReference;
